import type { CoreProduct, CoreProductPayload } from 'skladon-types';
import {
    CoreProductsGridOutput,
    fromCoreProductNamesV21,
    GridInput,
    GridOutput,
    toCoreProductNamesV21,
} from 'skladon-types';

import { ProductsMapper } from '../ProductsMapper';
import { CoreGridMapper } from '../CoreGridMapper';
import { asInt, asNumber } from './coerceNumbers';

interface ProductSetItem {
    sku: string;
    count: number;
}

interface ProductV21Input {
    productSetItems?: ProductSetItem[];
    [key: string]: unknown;
}

type CoreProductWithSet = Partial<CoreProduct> & { PRODUCT_SET?: unknown };

function mapProductSetToItems(coreProduct: CoreProductWithSet): ProductSetItem[] | undefined {
    const raw = coreProduct.PRODUCT_SET;
    if (!Array.isArray(raw) || raw.length === 0) {
        return undefined;
    }
    const items: ProductSetItem[] = [];
    for (const row of raw) {
        if (!row || typeof row !== 'object') {
            continue;
        }
        const r = row as Record<string, unknown>;
        const sku = r.PRODUCT_SKU != null ? String(r.PRODUCT_SKU) : '';
        if (!sku) {
            continue;
        }
        const countRaw = r.PRODUCT_COUNT;
        const count = typeof countRaw === 'number' ? countRaw : Number(countRaw);
        items.push({ sku, count: Number.isFinite(count) ? count : 0 });
    }
    if (items.length === 0) {
        return undefined;
    }
    items.sort((a, b) => a.sku.localeCompare(b.sku));
    return items;
}

const MONEY_AND_DIMS = [
    'price',
    'insertPriceFrom',
    'insertPriceTo',
    'weight',
    'width',
    'height',
    'length',
] as const;

const WHOLE_COUNTS = ['rop', 'expirationDays', 'quantityConversion'] as const;

const overlay = <T extends object>(
    source: T,
    keys: readonly string[],
    convert: (value: number | string | null) => number | null,
): T => {
    const next: Record<string, unknown> = { ...(source as Record<string, unknown>) };
    keys.forEach((key) => {
        if (!(key in next)) {
            return;
        }
        const value = next[key];
        if (value !== undefined) {
            next[key] = convert(value as number | string | null);
        }
    });
    return next as T;
};

const coerceProductNumbers = (product: Record<string, unknown>): Record<string, unknown> =>
    overlay(
        overlay(product, MONEY_AND_DIMS, (value) => asNumber(value) ?? null),
        WHOLE_COUNTS,
        (value) => asInt(value) ?? null,
    );

const AVAILABILITY_QTY = [
    'all',
    'awaiting',
    'onExpedition',
    'onHoldQuantity',
    'dutyQuantity',
    'allocated',
    'available',
] as const;

const coerceAvailabilityQuantities = <T extends object>(row: T): T =>
    overlay(row, AVAILABILITY_QTY, (value) => asNumber(value) ?? null);

export class ProductsMapperV21 {
    public static fromCore(coreProduct: Partial<CoreProduct>): Record<string, unknown> {
        const base = ProductsMapper.fromCore(coreProduct);
        const { scanSerialNumber, ...withoutDeprecated } = base as Record<string, unknown>;
        void scanSerialNumber;
        const coerced = coerceProductNumbers(withoutDeprecated);
        const productSetItems = mapProductSetToItems(coreProduct as CoreProductWithSet);
        return productSetItems?.length
            ? { ...coerced, productSetItems }
            : coerced;
    }

    public static toCore(product: ProductV21Input, ownerEshopKey: string): CoreProductPayload & { PRODUCT_SET?: { PRODUCT_SKU: string; PRODUCT_COUNT: number }[] } {
        const basePayload = ProductsMapper.toCore(product as unknown as Parameters<typeof ProductsMapper.toCore>[0], ownerEshopKey);
        const result: CoreProductPayload & { PRODUCT_SET?: { PRODUCT_SKU: string; PRODUCT_COUNT: number }[] } = { ...basePayload };

        if (product.productSetItems && product.productSetItems.length > 0) {
            result.PRODUCT_SET = product.productSetItems.map((item) => ({
                PRODUCT_SKU: item.sku,
                PRODUCT_COUNT: item.count,
            }));
        }

        return result;
    }

    public static fromCoreGrid(grid: CoreProductsGridOutput): GridOutput {
        return {
            data: grid.data
                .map((coreProduct) => ProductsMapperV21.fromCore(coreProduct))
                .map((product) =>
                    CoreGridMapper.omitColumns(product, fromCoreProductNamesV21, grid.request.columns)
                ),
            request: CoreGridMapper.mapGrid(grid.request, fromCoreProductNamesV21, 1000, ['active', 'ageCheck']),
            totalCount: grid.totalCount,
        };
    }

    public static toCoreGrid(grid: GridInput) {
        return CoreGridMapper.mapGrid(grid, toCoreProductNamesV21, 1000);
    }

    public static fromCoreAvailability(
        productAvailabilities: Parameters<typeof ProductsMapper.fromCoreAvailability>[0],
    ) {
        return ProductsMapper.fromCoreAvailability(productAvailabilities).map((row) => {
            const coerced = coerceAvailabilityQuantities(row);
            return {
                ...coerced,
                items: row.items?.map((item) => coerceAvailabilityQuantities(item)),
            };
        });
    }
    public static toCoreAvailability = ProductsMapper.toCoreAvailability;
}
